/** Start optional tags after the first visible main image and shopping controls. */
(() => {
  const configuration = document.currentScript;
  if (!configuration || configuration.dataset.state) return;
  configuration.dataset.state = 'waiting';

  const claritySource = `https://www.clarity.ms/tag/${configuration.dataset.clarityProject}?ref=shopify`;
  const attentiveSource = configuration.dataset.attentiveSrc;
  const alreadyPresent = (source) => [...document.scripts].some((script) => (
    script !== configuration && script.src.split('?')[0] === source.split('?')[0]
  ));

  // Preserve the app embed's consent queue while its network tag waits.
  if (!alreadyPresent(claritySource)) {
    window.clarity = window.clarity || function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return;
      window.clarity('consentv2', {
        ad_Storage: window.Shopify.customerPrivacy.marketingAllowed() ? 'granted' : 'denied',
        analytics_Storage: window.Shopify.customerPrivacy.analyticsProcessingAllowed() ? 'granted' : 'denied',
        source: 101,
      });
    });
    document.addEventListener('visitorConsentCollected', (event) => {
      window.clarity('consentv2', {
        ad_Storage: event.detail.marketingAllowed ? 'granted' : 'denied',
        analytics_Storage: event.detail.analyticsAllowed ? 'granted' : 'denied',
        source: 101,
      });
    });
  }

  const domReady = document.readyState === 'loading'
    ? new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
    : Promise.resolve();

  function mainImage() {
    const productImage = document.querySelector('#MainContent media-gallery .product-media__image');
    if (productImage) return productImage;
    return [...document.querySelectorAll('#MainContent img[fetchpriority="high"]')].find((image) => {
      const box = image.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight;
    });
  }

  function imageReady(image) {
    if (!image || image.complete) return Promise.resolve();
    return new Promise((resolve) => {
      const finish = () => {
        clearTimeout(deadline);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        resolve();
      };
      const deadline = setTimeout(finish, 5000);
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    });
  }

  function controlsReady() {
    const tags = ['variant-picker', 'product-form-component'].filter((tag) => (
      document.querySelector(`#MainContent ${tag}`)
    ));
    return new Promise((resolve) => {
      const deadline = setTimeout(resolve, 2000);
      Promise.all(tags.map((tag) => customElements.whenDefined(tag))).then(() => {
        clearTimeout(deadline);
        resolve();
      });
    });
  }

  function imagePainted(image) {
    if (!image || !image.naturalWidth || !window.PerformanceObserver?.supportedEntryTypes.includes('largest-contentful-paint')) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const finish = () => {
        clearTimeout(deadline);
        observer.disconnect();
        resolve();
      };
      const observer = new PerformanceObserver((entries) => {
        if (entries.getEntries().some((entry) => entry.element === image || entry.element?.contains(image))) finish();
      });
      // Browsers stop reporting LCP after interaction; keep optional tags usable.
      const deadline = setTimeout(finish, 4000);
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    });
  }

  function whenVisible(callback) {
    if (!document.hidden) {
      callback();
      return;
    }
    const onVisibility = () => {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onVisibility);
      callback();
    };
    document.addEventListener('visibilitychange', onVisibility);
  }

  function afterPaint(callback) {
    whenVisible(() => requestAnimationFrame(() => requestAnimationFrame(callback)));
  }

  function startTags() {
    configuration.dataset.state = 'ready';
    performance.mark('henry-optional-widgets:ready');
    const pending = [
      ['clarity', claritySource],
      ['attentive', attentiveSource],
    ];
    const next = () => whenVisible(() => {
      const start = () => {
        if (document.hidden) {
          next();
          return;
        }
        const [name, source] = pending.shift();
        if (!alreadyPresent(source)) {
          const script = document.createElement('script');
          script.async = true;
          script.src = source;
          script.dataset.henryOptionalWidget = name;
          performance.mark(`henry-optional-widgets:${name}`);
          document.head.appendChild(script);
        }
        if (pending.length) afterPaint(next);
        else configuration.dataset.state = 'started';
      };
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
      else setTimeout(start, 100);
    });
    next();
  }

  domReady.then(() => {
    const image = mainImage();
    return Promise.all([imageReady(image), controlsReady()]).then(() => imagePainted(image));
  }).then(() => afterPaint(startTags));
})();
